import { WORLD_CONFIG } from '../../config/world.config';
import { AD_GANTRY } from '../../level/segments/definitions/SEG_AD_GANTRY_40M';
import { PIPES, SEG_PIPES_40M } from '../../level/segments/definitions/SEG_PIPES_40M';
import { COMBO } from '../../level/segments/definitions/SEG_COMBO_40M';
import { DOZER_SET_PIECE, SEG_DOZER_60M } from '../../level/segments/definitions/SEG_DOZER_60M';
import { EARTHWORKS, SEG_EARTHWORKS_40M } from '../../level/segments/definitions/SEG_EARTHWORKS_40M';
import { PIT_LARGE, SEG_PIT_LARGE_40M } from '../../level/segments/definitions/SEG_PIT_LARGE';
import { PIT_SMALL, PIT_SMALL_SEGMENTS } from '../../level/segments/definitions/SEG_PIT_SMALL';
import { RAMP, SEG_RAMP_40M } from '../../level/segments/definitions/SEG_RAMP_40M';
import { SCAFFOLD } from '../../level/segments/definitions/SEG_SCAFFOLD_40M';
import { laneCenterX } from '../../level/segments/segmentBuilders';
import type { CeilingZone } from '../../level/segments/SegmentDefinition';
import { CONTAINER_OBSTACLE } from '../../obstacles/ObstacleCatalog';
import { AssetCategory, GreyboxMaterialId as M, type AssetManifestEntry, type GreyboxPart } from '../AssetTypes';
import { box, collapseSegmentGreybox, cylinder, junctionGreybox, pipeAlongZ, sphere, straightSegmentGreybox, workerGreybox, type JunctionOpenings } from './greybox';
import { CURVE_MODULE_ASSETS } from './curveGreybox';
import { dozerGreybox } from './dozerGreybox';
import { ENCLOSURE_MODULE_ASSETS } from './enclosureGreybox';
import { cableDrumGreybox, materialStackGreybox, pipePassageGreybox, pitSegmentGreybox, rampGreybox, scaffoldPassageGreybox } from './terrainGreybox';

/**
 * Central asset manifest – the only place where asset ids are mapped to
 * sources. To replace a greybox with the final Blender export, change
 * `source` to { kind: 'glb', url: '/assets/models/<folder>/<id lowercase>.glb' }
 * (keep the greybox parts as `fallback`). Gameplay code stays untouched.
 */
const greybox = (parts: readonly GreyboxPart[]) => ({ kind: 'greybox', parts }) as const;
const NO_COLLISION = { kind: 'none' } as const;
const GAMEPLAY_COLLISION = { kind: 'gameplay-data' } as const;

const bottleParts = (body: typeof M.Bottle | typeof M.Golden, scale = 1): GreyboxPart[] => [
  cylinder(0.2 * scale, 0.36 * scale, 0, 0, 0, body),
  cylinder(0.09 * scale, 0.2 * scale, 0, 0.36 * scale, 0, body),
  cylinder(0.1 * scale, 0.04 * scale, 0, 0.56 * scale, 0, M.BottleCap),
];

/**
 * Junction module built in Blender (tools/blender/segments/<design>.py via junction_module.py) in the
 * site standard of SEG_01; the greybox stays as fallback. The closed far side adds signs + ad scaffold.
 */
const junction = (id: string, design: string, description: string, open: JunctionOpenings): AssetManifestEntry => ({
  id,
  category: AssetCategory.TrackModule,
  source: { kind: 'glb', url: `/assets/models/track-modules/${id.toLowerCase()}.glb`, fallback: junctionGreybox(WORLD_CONFIG, 20, open) },
  collision: NO_COLLISION,
  preload: 'run',
  instancing: 'instances',
  metadata: {
    description,
    status: 'wip',
    triangleBudget: 14000,
    materialBudget: 18,
    byteBudget: 3 * 1024 * 1024,
    blenderSource: `design/01_segments/${design}/${design}.blend`,
  },
});

/**
 * Terrain module built in Blender: the GLB replaces the greybox, which stays as fallback.
 * Pits are full road modules (tools/blender/segments/SEG_04_Pit_Small_*.py via pit_module.py);
 * the 40 m set pieces are overlays on two SEG_STRAIGHT_20M road modules (tools/blender/assets/ENV_*.py),
 * modelled from the segment start with the dimensions of the segment definitions.
 */
const blenderTerrainModule = (
  id: string,
  design: string,
  description: string,
  url: string,
  parts: readonly GreyboxPart[],
  budget: { readonly triangles: number; readonly materials: number; readonly megabytes: number },
): AssetManifestEntry => ({
  id,
  category: AssetCategory.TrackModule,
  source: { kind: 'glb', url, fallback: parts },
  collision: NO_COLLISION,
  preload: 'run',
  instancing: 'instances',
  metadata: {
    description,
    status: 'wip',
    triangleBudget: budget.triangles,
    materialBudget: budget.materials,
    byteBudget: budget.megabytes * 1024 * 1024,
    blenderSource: `design/${design}/${design.split('/').pop()}.blend`,
  },
});

/**
 * Roadside bay (ENV_ROADSIDE_*): dense site dressing for the sockets tagged 'roadside' right behind
 * the fence (segmentBuilders.ts: SITE_LAYOUT.roadside). Local frame like BLD_SHELL_A: origin = bay
 * centre on the ground, 10 m along the track on x (−5 … 5), front towards the track at z = +3.5,
 * back at z = −3.5 – nothing may leave that box (the front stands 0.4 m behind the fence ads).
 *
 * Built in Blender (tools/blender/assets/ENV_Roadside*_A.py on the shared kit ENV_RoadsideKit_A) and published
 * to /assets/models/environment/<id lowercase>.glb – `published: true` loads the GLB and keeps the greybox as
 * fallback (a missing file would fail the 'run' preload on every start).
 */
/** Draw calls per bay asset (instanced): façade/containers, steel, wood, concrete, banner/prints, glass, lights, decals. */
const ROADSIDE_BAY_MATERIAL_BUDGET = 8;

const roadsideBayAsset = (spec: {
  readonly id: string;
  /** Design folder / Blender file name, e.g. ENV_RoadsideScaffold_A. */
  readonly design: string;
  readonly description: string;
  readonly parts: readonly GreyboxPart[];
  readonly triangleBudget: number;
  readonly published?: boolean;
}): AssetManifestEntry => ({
  id: spec.id,
  category: AssetCategory.Environment,
  source: spec.published ? { kind: 'glb', url: `/assets/models/environment/${spec.id.toLowerCase()}.glb`, fallback: spec.parts } : greybox(spec.parts),
  collision: NO_COLLISION,
  preload: 'run',
  instancing: 'instances',
  metadata: {
    description: spec.description,
    status: 'wip',
    triangleBudget: spec.triangleBudget,
    materialBudget: ROADSIDE_BAY_MATERIAL_BUDGET,
    blenderSource: `design/05_environment/${spec.design}/${spec.design}.blend`,
    tags: ['roadside'],
  },
});

/** Greybox pallet with load (bottom at y 0): wooden pallet + wrapped load on top. */
const palletLoad = (x: number, z: number, loadHeight: number): GreyboxPart[] => [
  box([1.2, 0.15, 1.0], x, 0, z, M.Wood),
  box([1.1, loadHeight, 0.9], x, 0.15, z, M.Concrete),
];

/** Greybox of OBS_CONTAINER_A: container body on two sleepers, roof band at the walkable top (CONTAINER_OBSTACLE). */
function containerObstacleGreybox(): GreyboxPart[] {
  const { width, length, height, sleeperHeight } = CONTAINER_OBSTACLE;
  const sleeperDepth = 0.16;
  const sleeperInset = 0.43; // sleeper centres under the container ends
  const roofBand = 0.14;
  return [
    box([width, height - sleeperHeight, length], 0, sleeperHeight, 0, M.Container),
    ...[-1, 1].map((end) => box([width, sleeperHeight, sleeperDepth], 0, 0, end * (length / 2 - sleeperInset), M.Wood)),
    box([width + 0.04, roofBand, length + 0.04], 0, height - roofBand, 0, M.ContainerAlt),
  ];
}

/** Concrete pipe lying along the track (local x), resting on the ground at y 0. */
const pipeAlongX = (diameter: number, length: number, x: number, z: number, y = 0): GreyboxPart => ({
  shape: 'cylinder',
  size: [diameter, length, diameter],
  position: [x, y + diameter / 2, z],
  rotation: [0, 0, 90],
  material: M.Concrete,
});

const ROADSIDE_BAYS: readonly AssetManifestEntry[] = [
  roadsideBayAsset({
    id: 'ENV_ROADSIDE_SCAFFOLD_A',
    design: 'ENV_RoadsideScaffold_A',
    description: 'Randzone: eingerüstete Rohbaufassade 10 m, 8–9 m hoch, Gerüstnetz und großes Banner zur Strecke',
    parts: [
      box([10, 8.4, 2.0], 0, 0, -2.5, M.Concrete), // shell façade behind the scaffold (z −3.5 … −1.5)
      box([10, 0.06, 1.2], 0, 2, -0.75, M.Wood), // first deck, open below the netting
      ...[-4.9, 0, 4.9].map((x) => box([0.08, 9.2, 0.08], x, 0, -0.1, M.Steel)), // standards
      box([10, 7.0, 0.05], 0, 2.2, 0, M.Fence), // netting from the first deck up
      box([6, 2.5, 0.04], 0, 4.2, 0.06, M.Container), // banner on the netting, facing the track
    ],
    triangleBudget: 9000,
    published: true,
  }),
  roadsideBayAsset({
    id: 'ENV_ROADSIDE_CONTAINERS_A',
    design: 'ENV_RoadsideContainers_A',
    description: 'Randzone: gebrauchte 20-Fuß-Container in vier Farben, bis dreilagig gestapelt, mit Rost, Schmutz und Beschriftung',
    parts: [
      box([6.06, 2.59, 2.44], -1.8, 0, -2.0, M.ContainerAlt),
      box([6.06, 2.59, 2.44], -1.8, 2.59, -2.0, M.ContainerAlt),
      box([6.06, 2.59, 2.44], -1.8, 0, 0.6, M.ContainerAlt),
      box([6.06, 2.59, 2.44], -1.8, 2.59, 0.6, M.Container),
      box([2.44, 2.59, 6.06], 3.6, 0, -0.4, M.ContainerAlt),
    ],
    triangleBudget: 10000,
    published: true,
  }),
  roadsideBayAsset({
    id: 'ENV_ROADSIDE_YARD_A',
    design: 'ENV_RoadsideYard_A',
    description: 'Randzone: Materiallager – Palettenstapel, Betonrohre, Betonblöcke, Baustahl-Bündel auf Kanthölzern',
    parts: [
      box([6, 0.45, 1.3], -1.8, 0, -2.6, M.Steel), // rebar bundle
      box([1.6, 0.8, 0.8], 3.6, 0, -2.6, M.Concrete), // concrete blocks
      box([1.6, 0.8, 0.8], 3.6, 0.8, -2.6, M.Concrete),
      box([1.6, 0.8, 0.8], 3.6, 0, -1.6, M.Concrete),
      pipeAlongX(0.9, 4, -2.4, -0.9),
      pipeAlongX(0.9, 4, -2.4, 0.05),
      pipeAlongX(0.9, 4, -2.4, -0.42, 0.78),
      ...palletLoad(-3.6, 2.4, 0.9),
      ...palletLoad(-2.1, 2.4, 0.6),
      ...palletLoad(1.6, 2.2, 1.1),
      ...palletLoad(3.2, 2.2, 0.8),
    ],
    triangleBudget: 14000,
    published: true,
  }),
  roadsideBayAsset({
    id: 'ENV_ROADSIDE_YARD_LIT_A',
    design: 'ENV_RoadsideYardLit_A',
    description: 'Randzone Nacht: Materiallager mit Lichtmast auf Anhänger (Fluter zur Strecke) und Stromaggregat',
    parts: [
      box([2.0, 1.1, 1.3], 2.8, 0.4, 0.6, M.Container), // light mast trailer
      box([0.15, 7.4, 0.15], 2.8, 1.5, 0.3, M.Steel), // mast
      box([1.6, 0.45, 0.25], 2.8, 8.9, 0.45, M.Lamp), // floods towards the track
      box([2.2, 1.35, 1.0], -0.4, 0, 2.3, M.Container), // generator
      box([6, 0.45, 1.3], -1.6, 0, -2.6, M.Steel), // rebar bundle
      pipeAlongX(0.9, 4, -2.6, -0.8),
      pipeAlongX(0.9, 4, -2.6, 0.15),
      ...palletLoad(-3.8, 2.4, 0.9),
      ...palletLoad(4.0, -2.4, 1.1),
    ],
    triangleBudget: 13000,
    published: true,
  }),
  roadsideBayAsset({
    id: 'ENV_ROADSIDE_OFFICE_A',
    design: 'ENV_RoadsideOffice_A',
    description: 'Randzone: Baustellenbüro aus zwei gestapelten Bürocontainern mit Außentreppe, Fensterband, Dixi-Toilette',
    parts: [
      box([6.06, 2.8, 2.44], -1.6, 0, -1.4, M.Drywall),
      box([6.06, 2.8, 2.44], -1.6, 2.8, -1.4, M.Drywall),
      box([4.4, 0.9, 0.04], -1.6, 1.1, -0.16, M.Lamp), // window bands (lit at night)
      box([4.4, 0.9, 0.04], -1.6, 3.9, -0.16, M.Lamp),
      box([1.4, 0.08, 2.44], 2.15, 2.8, -1.4, M.Steel), // landing at the upper door
      { shape: 'box', size: [3.4, 0.12, 1.0], position: [3.3, 1.4, -0.6], rotation: [0, 0, -40], material: M.Steel }, // stairs
      box([1.1, 2.3, 1.1], 4.2, 0, 2.6, M.ContainerAlt), // portable toilet
    ],
    triangleBudget: 7000,
    published: true,
  }),
];

/**
 * Ad gantry over the road (SEG_AD_GANTRY_40M, design ENV_AdGantry_A) – geometry contract in AD_GANTRY:
 * origin = track centre on the ground, x across, z along the travel direction, symmetric about z = 0.
 * Two lattice towers on ballast blocks at x ±5.2, truss beams, dark banner backing (front face z −0.10).
 * The banner image is not part of the model: the game draws it as ad surface (AD_GANTRY_BANNER) in front.
 */
const AD_GANTRY_ASSET_SPEC = {
  /**
   * The GLB is published (tools/blender/assets/ENV_AdGantry_A.py → /assets/models/environment/,
   * 2026-10-02); false falls back to the greybox (a missing GLB fails the 'run' preload).
   */
  published: true,
  /** Lattice towers, gussets and bracing of a 10.4 m truss gantry. */
  triangleBudget: 14000,
  /** Steel (galvanised), concrete ballast, banner backing, bolts/plates, signs, floodlights. */
  materialBudget: 6,
  byteBudget: 2 * 1024 * 1024,
} as const;

function adGantryGreybox(): GreyboxPart[] {
  const { tower, ballast, beams, banner } = AD_GANTRY;
  const span = 2 * tower.x; // beams run tower axis to tower axis
  const innerGap = 2 * tower.x - tower.section;
  const backingDepth = 0.1;
  return [
    ...[-1, 1].flatMap((side) => [
      box([ballast.width, ballast.height, ballast.depth], side * tower.x, 0, 0, M.Concrete),
      box([tower.section, tower.height - ballast.height, tower.section], side * tower.x, ballast.height, 0, M.Steel),
    ]),
    box([span, beams.lower.to - beams.lower.from, beams.depth], 0, beams.lower.from, 0, M.Steel),
    box([span, beams.top.to - beams.top.from, beams.depth], 0, beams.top.from, 0, M.Steel),
    // dark backing between the beams, front face at banner.backingZ (the ad surface hangs 2 cm in front)
    box([innerGap, beams.top.from - beams.lower.to, backingDepth], 0, beams.lower.to, banner.backingZ + backingDepth / 2, M.ConcreteDark),
  ];
}

const AD_GANTRY_ENTRY: AssetManifestEntry = {
  id: AD_GANTRY.assetId,
  category: AssetCategory.Environment,
  source: AD_GANTRY_ASSET_SPEC.published
    ? { kind: 'glb', url: `/assets/models/environment/${AD_GANTRY.assetId.toLowerCase()}.glb`, fallback: adGantryGreybox() }
    : greybox(adGantryGreybox()),
  collision: NO_COLLISION,
  preload: 'run',
  instancing: 'instances',
  metadata: {
    description:
      'Werbebrücke über die Fahrbahn: zwei Gitterturm-Stützen (x ±5,2 m, 8,9 m) auf Betonballast, Fachwerk-Riegel oben und unten, ' +
      'dunkle Bannerrückwand für das 9,9 × 2,5 m Werbebanner (Banner-Unterkante 5,5 m, Riegel-Unterkante 4,9 m) – das Motiv tauscht das Spiel als Werbefläche',
    status: 'wip',
    triangleBudget: AD_GANTRY_ASSET_SPEC.triangleBudget,
    materialBudget: AD_GANTRY_ASSET_SPEC.materialBudget,
    byteBudget: AD_GANTRY_ASSET_SPEC.byteBudget,
    blenderSource: 'tools/blender/assets/ENV_AdGantry_A.py',
    tags: ['landmark', 'advertising'],
  },
};

/**
 * VEH_DOZER_A – compact crawler dozer of the dozer set piece (SEG_DOZER_60M), rigged for the game (dozer/dozerRig.ts).
 * Published by tools/blender/assets/VEH_05_Catapillar.py (2026-10-02); the rigged greybox – same node names – stands
 * in while the GLB is not loaded (false: greybox only).
 */
const DOZER_ASSET_SPEC = {
  published: true,
  /** The rider sits on it right in front of the camera: grab bars, hoses and levers need the triangles (41 618). */
  triangleBudget: 42000,
  /** MAT_DOZER_BODY (Meshy base map) and MAT_DOZER_TRACK (scrolling belt). */
  materialBudget: 2,
  byteBudget: 2 * 1024 * 1024,
} as const;

const DOZER_GREYBOX = dozerGreybox();

const DOZER_ENTRY: AssetManifestEntry = {
  id: 'VEH_DOZER_A',
  category: AssetCategory.Vehicle,
  source: DOZER_ASSET_SPEC.published
    ? { kind: 'glb', url: '/assets/models/vehicles/veh_dozer_a.glb', fallback: DOZER_GREYBOX.parts, fallbackRig: DOZER_GREYBOX.rig }
    : { kind: 'greybox', parts: DOZER_GREYBOX.parts, rig: DOZER_GREYBOX.rig },
  // the gameplay colliders are OBS_DOZER_A's (DOZER_CONFIG.vehicle.colliders)
  collision: { kind: 'gameplay-data' },
  preload: 'run',
  instancing: 'instances',
  metadata: {
    description:
      'Kompakte Planierraupe (4,2 × 2,34 × 2,06 m): offener Fahrersitz (Sitzfläche 1,48 m), Schild an Schubarmen mit zwei Hubzylindern, ' +
      'Gummiketten (Textur läuft), Rig: Schild-Drehpunkt, Zylinder, Ketten, Räder, Sockel Sitz/Schneide/Auspuff',
    status: 'wip',
    triangleBudget: DOZER_ASSET_SPEC.triangleBudget,
    materialBudget: DOZER_ASSET_SPEC.materialBudget,
    byteBudget: DOZER_ASSET_SPEC.byteBudget,
    blenderSource: 'design/02_vehicles/VEH_05_Catapillar/VEH_05_Catapillar.blend',
    tags: ['dozer', 'vehicle'],
  },
};

/** Pit road module: asphalt with the hole, shored pit, markings, barriers and fence (site standard of SEG_01). */
const PIT_MODULE_BUDGET = { triangles: 14000, materials: 18, megabytes: 3 } as const;
/** The dozer module: a pit module 60 m long – barriers and fence along half as much road again as SEG_PIT_LARGE. */
const DOZER_MODULE_BUDGET = { triangles: 20000, materials: 18, megabytes: 3 } as const;
/** Overlay set pieces on two road modules. */
const OVERLAY_BUDGET = { triangles: 16000, materials: 6, megabytes: 1 } as const;

const pipeCeiling = (id: string): CeilingZone => {
  const zone = SEG_PIPES_40M.terrain?.ceilings.find((c) => c.id === id);
  if (!zone) throw new Error(`SEG_PIPES_40M has no ceiling ${id}`);
  return zone;
};

const pipeBridge = (name: string, z: number) => {
  const deck = pipeCeiling(`CEIL_${name}`);
  const post = pipeCeiling(`CEIL_${name}_POST`);
  return { z, xFrom: deck.xFrom, xTo: deck.xTo, postX: (post.xFrom + post.xTo) / 2 };
};

const rampSurface = SEG_RAMP_40M.terrain?.surfaces.find((s) => s.id === 'SURF_PLATFORM');

const TERRAIN_MODULES: readonly AssetManifestEntry[] = [
  ...PIT_SMALL_SEGMENTS.map((segment) =>
    blenderTerrainModule(
      segment.id,
      // design folder per variant: SEG_PIT_SMALL_C_20M -> 01_segments/SEG_04_Pit_Small_C
      `01_segments/SEG_04_Pit_Small_${segment.id.replace('SEG_PIT_SMALL_', '').replace('_20M', '')}`,
      `SEG_04 ${segment.displayName}: Baugrube ${PIT_SMALL.zTo - PIT_SMALL.zFrom} m, Berliner Verbau (Stahlträger + Holzbohlen), Steifen, Leiter, Pfütze, Warnmarkierung am Rand`,
      `/assets/models/track-modules/${segment.id.toLowerCase()}.glb`,
      pitSegmentGreybox(WORLD_CONFIG, segment.length, segment.terrain?.gaps ?? []),
      PIT_MODULE_BUDGET,
    ),
  ),
  blenderTerrainModule(
    'SEG_PIT_LARGE_40M',
    '01_segments/SEG_05_Pit_Large',
    `SEG_05 Baugrube groß: ${PIT_LARGE.pit.zTo - PIT_LARGE.pit.zFrom} m über alle Spuren, ${PIT_LARGE.pit.depth} m tief, Betonblock-Plattformen links/rechts, Stahlträger-Brücke Mitte`,
    '/assets/models/track-modules/seg_pit_large_40m.glb',
    pitSegmentGreybox(WORLD_CONFIG, SEG_PIT_LARGE_40M.length, SEG_PIT_LARGE_40M.terrain?.gaps ?? []),
    PIT_MODULE_BUDGET,
  ),
  blenderTerrainModule(
    'SEG_EARTHWORKS_40M',
    '01_segments/SEG_21_Earthworks',
    `SEG_21 Erdarbeiten: Baustraße aus verdichteter Erde, Graben ${EARTHWORKS.trench.zTo - EARTHWORKS.trench.zFrom} m über alle Spuren, Schlammpfützen, Absteckpflöcke, Nadelbäume`,
    '/assets/models/track-modules/seg_earthworks_40m.glb',
    pitSegmentGreybox(WORLD_CONFIG, SEG_EARTHWORKS_40M.length, SEG_EARTHWORKS_40M.terrain?.gaps ?? []),
    { ...PIT_MODULE_BUDGET, triangles: 18000 }, // 40 m of barriers and fence plus conifers
  ),
  blenderTerrainModule(
    'SEG_COMBO_40M',
    '05_environment/ENV_ComboSet_A',
    'SEG_23 Spezial-Kombi (Overlay, ENV_ComboSet_A): Durchfahrt „ACHTUNG NIEDRIGE DURCHFAHRT" 1,4 m (Rutschen), Gerüstrampe 1,2 m mit Plattform (Risiko-Route)',
    '/assets/models/track-modules/seg_combo_40m.glb',
    [
      ...rampGreybox({
        xFrom: -WORLD_CONFIG.laneWidth / 2,
        xTo: WORLD_CONFIG.laneWidth / 2,
        ramp: COMBO.ramp,
        platform: COMBO.platform,
        height: COMBO.ramp.height,
      }),
      box([2 * COMBO.halfSpan, 0.5, COMBO.lowPassage.depth], 0, COMBO.lowPassage.clearance, COMBO.lowPassage.z, M.Chevron),
      box([0.3, COMBO.lowPassage.clearance, 0.3], -COMBO.halfSpan - 0.3, 0, COMBO.lowPassage.z, M.Steel),
      box([0.3, COMBO.lowPassage.clearance, 0.3], COMBO.halfSpan + 0.3, 0, COMBO.lowPassage.z, M.Steel),
    ],
    { triangles: 6000, materials: 9, megabytes: 1 }, // ramp kit + gantry, sign, lights
  ),
  blenderTerrainModule(
    'SEG_SCAFFOLD_40M',
    '05_environment/ENV_ScaffoldPassage_A',
    'SEG_06 Gerüstpassage (Overlay, ENV_ScaffoldPassage_A): Fassadengerüste, Belag 3,9 m, gestreifte Querriegel 1,6 m (Rutschen), Arbeitsleuchten',
    '/assets/models/track-modules/seg_scaffold_40m.glb',
    scaffoldPassageGreybox({ deck: SCAFFOLD.deck, bar: SCAFFOLD.bar, bars: Object.values(SCAFFOLD.bars), halfSpan: SCAFFOLD.halfSpan }),
    OVERLAY_BUDGET,
  ),
  blenderTerrainModule(
    'SEG_PIPES_40M',
    '05_environment/ENV_PipePassage_A',
    'SEG_07 Rohrpassage (Overlay, ENV_PipePassage_A): begehbare Betonrohre DN 2000 mit Muffen, Erdbett, Stahl-Rohrbrücken 1,3 m mit Warnmanschetten (Rutschen)',
    '/assets/models/track-modules/seg_pipes_40m.glb',
    pipePassageGreybox({
      pipes: [PIPES.pipeA, PIPES.pipeB].map((p) => ({ x: laneCenterX(WORLD_CONFIG, p.lane), zFrom: p.zFrom, zTo: p.zTo })),
      innerRadius: PIPES.innerRadius,
      wall: PIPES.wall,
      bridges: [pipeBridge('BRIDGE_A', PIPES.bridgeA.z), pipeBridge('BRIDGE_B', PIPES.bridgeB.z)],
      bridgeClearance: PIPES.bridge.clearance,
      bridgeDiameter: PIPES.bridge.depth,
    }),
    OVERLAY_BUDGET,
  ),
  blenderTerrainModule(
    'SEG_RAMP_40M',
    '05_environment/ENV_Ramp_A',
    'SEG_08 Rampe (Overlay, ENV_Ramp_A): Gerüstrampe mit Stahlbelag und Leitpfeilen, Plattform 1,8 m, Warnstreifen an den Kanten',
    '/assets/models/track-modules/seg_ramp_40m.glb',
    rampGreybox({
      xFrom: rampSurface?.xFrom ?? -WORLD_CONFIG.laneWidth / 2,
      xTo: rampSurface?.xTo ?? WORLD_CONFIG.laneWidth / 2,
      ramp: RAMP.ramp,
      platform: RAMP.platform,
      height: RAMP.height,
    }),
    OVERLAY_BUDGET,
  ),
];

export const ASSET_MANIFEST: readonly AssetManifestEntry[] = [
  // ── Track modules ────────────────────────────────────────────────────────
  {
    id: 'SEG_STRAIGHT_20M',
    category: AssetCategory.TrackModule,
    // Proof of Pipeline: built in Blender (tools/blender/segments/SEG_01_Straight_20M.py),
    // validated and published by tools/pipeline/publish-segment.mjs.
    source: {
      kind: 'glb',
      url: '/assets/models/track-modules/seg_straight_20m.glb',
      fallback: straightSegmentGreybox(WORLD_CONFIG, 20),
    },
    collision: NO_COLLISION,
    preload: 'critical',
    instancing: 'instances',
    metadata: {
      description: 'SEG_01 Gerade 20 m: Asphalt (Fototextur), 3 Spuren, Kunststoff-Leitwände rot/weiß, Bauzaun mit Werbeflächen, Schotter',
      status: 'wip',
      triangleBudget: 12000,
      materialBudget: 12,
      byteBudget: 2.5 * 1024 * 1024,
      blenderSource: 'design/01_segments/SEG_01_Straight_20M/SEG_01_Straight_20M.blend',
    },
  },

  junction('SEG_TURN_LEFT_20M', 'SEG_17_Turn_Input_Left', 'Abzweigung links: Zufahrt 13,4 m + Kreuzungsfeld 6,6 m, Pfeiltafeln, Werbegerüst', { left: true, right: false, straight: false }),
  junction('SEG_TURN_RIGHT_20M', 'SEG_18_Turn_Input_Right', 'Abzweigung rechts: Zufahrt 13,4 m + Kreuzungsfeld 6,6 m, Pfeiltafeln, Werbegerüst', { left: false, right: true, straight: false }),
  junction('SEG_JUNCTION_T_20M', 'SEG_19_Junction_T', 'T-Kreuzung links/rechts, Pfeiltafeln, Werbegerüst', { left: true, right: true, straight: false }),
  junction('SEG_CROSSING_20M', 'SEG_19_Intersection', 'Kreuzung links/rechts/geradeaus', { left: true, right: true, straight: true }),

  {
    id: 'SEG_COLLAPSE_40M',
    category: AssetCategory.TrackModule,
    // Built in Blender (tools/blender/segments/SEG_20_Collapse.py via collapse_module.py): road, open building plot,
    // standing ruin and warning sign; the collapsing parts are OBS_DEBRIS_* obstacles. Greybox stays as fallback.
    source: { kind: 'glb', url: '/assets/models/track-modules/seg_collapse_40m.glb', fallback: collapseSegmentGreybox(WORLD_CONFIG, 40) },
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Set Piece Hauseinsturz: Gebäude links stürzt auf die Strecke, Ruine bleibt stehen',
      status: 'wip',
      triangleBudget: 16000,
      materialBudget: 18,
      byteBudget: 3 * 1024 * 1024,
      blenderSource: 'design/01_segments/SEG_20_Collapse/SEG_20_Collapse.blend',
    },
  },

  ...TERRAIN_MODULES,
  // The whole 60 m dozer module (road, the shored pit across all lanes, barriers, fence) – built by the shared pit module
  // (tools/blender/segments/SEG_24_Dozer_Pit.py). Was a grey greybox pit block on two road modules (user 2026-10-03:
  // "Bei dem Loch ist keine Straße, das ist ein graues Feld").
  blenderTerrainModule(
    'SEG_DOZER_60M',
    '01_segments/SEG_24_Dozer_Pit',
    `Planierraupe – Grube zuschieben: Baugrube ${DOZER_SET_PIECE.pit.zTo - DOZER_SET_PIECE.pit.zFrom} m quer über alle Spuren, ${DOZER_SET_PIECE.pit.depth} m tief, Berliner Verbau – die Raupe füllt sie mit dem geschobenen Erdberg`,
    '/assets/models/track-modules/seg_dozer_60m.glb',
    pitSegmentGreybox(WORLD_CONFIG, SEG_DOZER_60M.length, SEG_DOZER_60M.terrain?.gaps ?? []),
    DOZER_MODULE_BUDGET,
  ),
  ...CURVE_MODULE_ASSETS,
  ...ENCLOSURE_MODULE_ASSETS,

  // ── Characters ───────────────────────────────────────────────────────────
  {
    id: 'CHAR_02_CARPENTER_M',
    category: AssetCategory.Character,
    // Rigged player character (SK_BAURUN_Humanoid_V1, BAURUN_RUNNER_V1 clips), built in Blender by
    // tools/blender/characters/CHAR_02_Carpenter_M.py, validated + published by build-character.mjs.
    source: {
      kind: 'glb',
      url: '/assets/models/characters/char_02_carpenter_m.glb',
      fallback: workerGreybox(M.WorkerVest, M.WorkerHelmet),
    },
    collision: GAMEPLAY_COLLISION,
    preload: 'critical',
    instancing: 'clones',
    metadata: {
      description: 'Spielfigur Zimmerer: Helm, Arbeitshemd mit Logo, Hosenträger, Werkzeuggürtel, Knieschoner, Arbeitsstiefel; Rücken = Werbefläche MAT_CHAR_AD_BACK',
      status: 'wip',
      triangleBudget: 36000,
      materialBudget: 15,
      byteBudget: 4 * 1024 * 1024,
      blenderSource: 'design/07_characters/CHAR_02_Carpenter_M/CHAR_02_Carpenter_M.blend',
    },
  },
  {
    id: 'CHAR_01_ELECTRICIAN_F',
    category: AssetCategory.Character,
    // Rigged player character on the same rig/clips as the carpenter
    // (tools/blender/characters/CHAR_01_Electrician_F.py); the blue-vest greybox stands in if the GLB fails.
    source: {
      kind: 'glb',
      url: '/assets/models/characters/char_01_electrician_f.glb',
      fallback: workerGreybox(M.TimeBonus, M.WorkerHelmet),
    },
    collision: GAMEPLAY_COLLISION,
    preload: 'critical',
    instancing: 'clones',
    metadata: {
      description: 'Spielfigur Elektrikerin (1,65 m): Helm, Arbeitskleidung, Werkzeuggürtel; Rücken = Werbefläche MAT_CHAR_AD_BACK',
      status: 'wip',
      triangleBudget: 36000,
      materialBudget: 15,
      byteBudget: 4 * 1024 * 1024,
      blenderSource: 'design/07_characters/CHAR_01_Electrician_F/CHAR_01_Electrician_F.blend',
    },
  },
  {
    id: 'CHAR_WORKER_PLAYER',
    category: AssetCategory.Character,
    source: greybox(workerGreybox(M.WorkerVest, M.WorkerHelmet)),
    collision: GAMEPLAY_COLLISION,
    preload: 'critical',
    instancing: 'clones',
    metadata: {
      description: 'Spielfigur Bauarbeiter (Platzhalter)',
      status: 'greybox',
      triangleBudget: 50000,
      blenderSource: 'blender/characters/char_worker_player.blend',
    },
  },

  // ── Obstacles ────────────────────────────────────────────────────────────
  {
    id: 'OBS_BARRIER_A',
    category: AssetCategory.Obstacle,
    // Built in Blender (tools/blender/assets/OBS_Barrier_A.py) from the shared ENV_Barrier_A meshes.
    source: {
      kind: 'glb',
      url: '/assets/models/obstacles/obs_barrier_a.glb',
      fallback: [box([2.4, 0.8, 0.5], 0, 0, 0, M.Barrier)],
    },
    collision: GAMEPLAY_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Betonleitwand: Kunststoff-Leitwand rot + weiß (Sprung)',
      status: 'wip',
      triangleBudget: 400,
      blenderSource: 'design/03_obstacles/OBS_Barrier_A/OBS_Barrier_A.blend',
    },
  },
  {
    id: 'OBS_CONE_ROW_A',
    category: AssetCategory.Obstacle,
    // Built in Blender (tools/blender/assets/OBS_ConeRow_A.py) from the shared ENV_Cone_A mesh.
    source: {
      kind: 'glb',
      url: '/assets/models/obstacles/obs_cone_row_a.glb',
      fallback: [-0.75, 0, 0.75].map((x) => cylinder(0.3, 0.75, x, 0, 0, M.Barrier)),
    },
    collision: GAMEPLAY_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Leitkegel-Reihe: 3 Leitkegel quer über die Spur (Sprung)',
      status: 'wip',
      triangleBudget: 600,
      blenderSource: 'design/03_obstacles/OBS_ConeRow_A/OBS_ConeRow_A.blend',
    },
  },
  {
    id: 'OBS_CONCRETE_PIPE_A',
    category: AssetCategory.Obstacle,
    // Built in Blender (tools/blender/assets/OBS_ConcretePipe_A.py).
    source: {
      kind: 'glb',
      url: '/assets/models/obstacles/obs_concrete_pipe_a.glb',
      fallback: [{ shape: 'cylinder', size: [1.12, 2, 1.12], position: [0, 0.56, 0], rotation: [0, 0, 90], material: M.Concrete }],
    },
    collision: GAMEPLAY_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Betonrohr DN 800 mit Muffe, quer liegend (Sprung)',
      status: 'wip',
      triangleBudget: 800,
      blenderSource: 'design/03_obstacles/OBS_ConcretePipe_A/OBS_ConcretePipe_A.blend',
    },
  },
  {
    id: 'OBS_BIG_BAG_A',
    category: AssetCategory.Obstacle,
    // Built in Blender (tools/blender/assets/OBS_BigBag_A.py).
    source: {
      kind: 'glb',
      url: '/assets/models/obstacles/obs_big_bag_a.glb',
      fallback: [box([1, 1.1, 1], 0, 0, 0, M.Concrete)],
    },
    collision: GAMEPLAY_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Big Bag mit Sand, Hebeschlaufen oben verknotet (Sprung)',
      status: 'wip',
      triangleBudget: 2600,
      blenderSource: 'design/03_obstacles/OBS_BigBag_A/OBS_BigBag_A.blend',
    },
  },
  {
    id: 'OBS_BRICK_STACK_A',
    category: AssetCategory.Obstacle,
    // Built in Blender (tools/blender/assets/OBS_BrickStack_A.py) on the shared ENV_Pallet_A mesh.
    source: {
      kind: 'glb',
      url: '/assets/models/obstacles/obs_brick_stack_a.glb',
      fallback: [box([1.25, 1.86, 0.82], 0, 0, 0, M.Crate)],
    },
    collision: GAMEPLAY_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Ziegelstapel: 3 Palettenladungen Hochlochziegel, umreift (Doppelsprung)',
      status: 'wip',
      triangleBudget: 2600,
      blenderSource: 'design/03_obstacles/OBS_BrickStack_A/OBS_BrickStack_A.blend',
    },
  },
  {
    id: 'OBS_PALLET_A',
    category: AssetCategory.Obstacle,
    // Built in Blender (tools/blender/assets/OBS_Pallet_A.py) from the shared ENV_Pallet_A mesh.
    source: {
      kind: 'glb',
      url: '/assets/models/obstacles/obs_pallet_a.glb',
      fallback: [box([1.2, 0.58, 0.8], 0, 0, 0, M.Wood)],
    },
    collision: GAMEPLAY_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Palettenstapel aus 4 Europaletten (Sprung)',
      status: 'wip',
      triangleBudget: 1200,
      blenderSource: 'design/03_obstacles/OBS_Pallet_A/OBS_Pallet_A.blend',
    },
  },
  {
    id: 'OBS_SCAFFOLD_PASS_A',
    category: AssetCategory.Obstacle,
    // Built in Blender (tools/blender/assets/OBS_ScaffoldPass_A.py) after SEG_06.
    source: {
      kind: 'glb',
      url: '/assets/models/obstacles/obs_scaffold_pass_a.glb',
      fallback: [box([2.3, 0.26, 0.26], 0, 1.6, 0, M.Chevron), box([2.2, 1.0, 0.9], 0, 2.9, 0, M.Wood)],
    },
    collision: GAMEPLAY_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Gerüstdurchgang: gestreifte Querstrebe 1,6 m Durchgang, Gerüstbelag darüber (Rutschen)',
      status: 'wip',
      triangleBudget: 1200,
      blenderSource: 'design/03_obstacles/OBS_ScaffoldPass_A/OBS_ScaffoldPass_A.blend',
    },
  },
  {
    id: 'OBS_BARRIER_BOARD_A',
    category: AssetCategory.Obstacle,
    // Built in Blender (tools/blender/assets/OBS_BarrierBoard_A.py) after the SEG_03 in-game view.
    source: {
      kind: 'glb',
      url: '/assets/models/obstacles/obs_barrier_board_a.glb',
      fallback: [box([2, 1, 0.3], 0, 0, 0, M.Barrier)],
    },
    collision: GAMEPLAY_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Absperrschranke rot-weiß mit Warnleuchte (Sprung)',
      status: 'wip',
      triangleBudget: 700,
      blenderSource: 'design/03_obstacles/OBS_BarrierBoard_A/OBS_BarrierBoard_A.blend',
    },
  },
  {
    id: 'OBS_SCAFFOLD_POLE_A',
    category: AssetCategory.Obstacle,
    // Built in Blender (tools/blender/assets/OBS_ScaffoldPole_A.py) after SEG_06.
    source: {
      kind: 'glb',
      url: '/assets/models/obstacles/obs_scaffold_pole_a.glb',
      fallback: [box([1.9, 0.66, 0.3], 0, 0, 0, M.Steel)],
    },
    collision: GAMEPLAY_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Gerüststange unten: Rohr-Hürde (Sprung)',
      status: 'wip',
      triangleBudget: 600,
      blenderSource: 'design/03_obstacles/OBS_ScaffoldPole_A/OBS_ScaffoldPole_A.blend',
    },
  },
  {
    id: 'OBS_PALLET_STACK_A',
    category: AssetCategory.Obstacle,
    source: greybox(
      [0, 1, 2, 3].flatMap((level) => [
        box([1.2, 0.15, 1], 0, level * 0.6, 0, M.Wood),
        box([1.1, 0.45, 0.9], 0, level * 0.6 + 0.15, 0, M.Concrete),
      ]),
    ),
    collision: GAMEPLAY_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: { description: 'Palettenstapel 2,4 m (Doppelsprung)', status: 'greybox', triangleBudget: 2500 },
  },
  {
    id: 'OBS_CONTAINER_A',
    category: AssetCategory.Obstacle,
    // Built in Blender (tools/blender/assets/OBS_Container_A.py, shared container kit): used 20 ft standard
    // container on two thin timber sleepers, doors towards the runner, walkable roof at 2.69 m (CONTAINER_OBSTACLE).
    // The visual is the real 2.44 m wide, the collider stays 2.0 m.
    source: {
      kind: 'glb',
      url: '/assets/models/obstacles/obs_container_a.glb',
      fallback: containerObstacleGreybox(),
    },
    collision: GAMEPLAY_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Container 2,69 m (Spurwechsel, Dach per Doppelsprung begehbar)',
      status: 'wip',
      triangleBudget: 2500,
      blenderSource: 'design/03_obstacles/OBS_Container_A/OBS_Container_A.blend',
    },
  },

  // Set-piece obstacles of the terrain modules (level variety phase 1).
  {
    id: 'OBS_DIRT_MOUND_A',
    category: AssetCategory.Obstacle,
    // Built in Blender (tools/blender/assets/OBS_DirtMound_A.py) after SEG_21 "Erdhaufen".
    source: { kind: 'glb', url: '/assets/models/obstacles/obs_dirt_mound_a.glb', fallback: [box([2.0, 0.9, 1.6], 0, 0, 0, M.Wood)] },
    collision: GAMEPLAY_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: { description: 'Erdhaufen mit Steinen 2,0 × 0,9 × 1,6 m (Sprung)', status: 'wip', triangleBudget: 2500, blenderSource: 'design/03_obstacles/OBS_DirtMound_A/OBS_DirtMound_A.blend' },
  },
  {
    id: 'OBS_MATERIAL_STACK_A',
    category: AssetCategory.Obstacle,
    // Built in Blender (tools/blender/assets/OBS_MaterialStack_A.py): pallets + concrete formwork blocks, strapped.
    source: { kind: 'glb', url: '/assets/models/obstacles/obs_material_stack_a.glb', fallback: materialStackGreybox() },
    collision: GAMEPLAY_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: { description: 'Materialstapel (Paletten + Schalungssteine, gegurtet) bis unter den Gerüstriegel 1,6 m (Spurwechsel)', status: 'wip', triangleBudget: 2500, blenderSource: 'design/03_obstacles/OBS_MaterialStack_A/OBS_MaterialStack_A.blend' },
  },
  {
    id: 'OBS_CABLE_DRUM_A',
    category: AssetCategory.Obstacle,
    // Built in Blender (tools/blender/assets/OBS_CableDrum_A.py) with the cable drum kit of ENV_CableReel_A.
    source: { kind: 'glb', url: '/assets/models/obstacles/obs_cable_drum_a.glb', fallback: cableDrumGreybox() },
    collision: GAMEPLAY_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: { description: 'Kabeltrommel klein Ø 0,9 m, Achse quer (Sprung)', status: 'wip', triangleBudget: 800, blenderSource: 'design/03_obstacles/OBS_CableDrum_A/OBS_CableDrum_A.blend' },
  },
  {
    // SEG_10 "Schutthaufen" – also the heap the excavator dumps into the lane (HazardPresenter lets it grow).
    id: 'OBS_RUBBLE_HEAP_A',
    category: AssetCategory.Obstacle,
    // Built in Blender (tools/blender/assets/OBS_RubbleHeap_A.py, debris_kit.py) – greybox stays as fallback.
    source: { kind: 'glb', url: '/assets/models/obstacles/obs_rubble_heap_a.glb', fallback: [
      box([1.9, 0.3, 1.7], 0, 0, 0, M.TrackSide),
      box([1.4, 0.25, 1.2], 0.05, 0.25, -0.05, M.TrackSide, [0, 12, 0]),
      box([0.8, 0.2, 0.7], -0.1, 0.48, 0.05, M.TrackSide, [0, -20, 0]),
      box([0.5, 0.3, 0.4], -0.55, 0.2, 0.45, M.Concrete, [12, 30, -8]),
      box([0.45, 0.25, 0.35], 0.6, 0.18, -0.4, M.Concrete, [-10, -25, 14]),
      box([0.35, 0.2, 0.5], 0.35, 0.42, 0.3, M.Concrete, [20, 40, 10]),
      box([0.04, 0.04, 1.1], 0.2, 0.5, -0.1, M.Steel, [-25, 30, 0]),
    ] },
    collision: GAMEPLAY_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: { description: 'Schutthaufen aus Erde, Betonbrocken und Bewehrung (Sprung)', status: 'wip', triangleBudget: 3000, blenderSource: 'design/03_obstacles/OBS_RubbleHeap_A/OBS_RubbleHeap_A.blend' },
  },

  // Debris of the collapsing building – also the building's walls/floors before the collapse.
  {
    id: 'OBS_DEBRIS_BLOCK_A',
    category: AssetCategory.Obstacle,
    // Built in Blender (tools/blender/assets/OBS_DebrisBlock_A.py, debris_kit.py) – greybox stays as fallback.
    source: { kind: 'glb', url: '/assets/models/obstacles/obs_debris_block_a.glb', fallback: [
      box([2, 0.9, 1.2], 0, 0, 0, M.Concrete),
      box([0.06, 0.06, 1.6], -0.5, 0.9, 0, M.Steel, [18, 0, 0]),
      box([0.06, 0.06, 1.4], 0.4, 0.9, 0.1, M.Steel, [-14, 20, 0]),
    ] },
    collision: GAMEPLAY_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: { description: 'Betontrümmer mit Bewehrung und Schutt (Sprung)', status: 'wip', triangleBudget: 3000, blenderSource: 'design/03_obstacles/OBS_DebrisBlock_A/OBS_DebrisBlock_A.blend' },
  },
  {
    id: 'OBS_DEBRIS_WALL_A',
    category: AssetCategory.Obstacle,
    // Built in Blender (tools/blender/assets/OBS_DebrisWall_A.py, debris_kit.py) – greybox stays as fallback.
    source: { kind: 'glb', url: '/assets/models/obstacles/obs_debris_wall_a.glb', fallback: [box([2, 3.4, 1], 0, 0, 0, M.Concrete), box([0.9, 1.1, 1.04], 0.25, 1.5, 0, M.Steel)] },
    collision: GAMEPLAY_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: { description: 'Wandstück mit Fensteröffnung auf Schutt (Spurwechsel)', status: 'wip', triangleBudget: 3000, blenderSource: 'design/03_obstacles/OBS_DebrisWall_A/OBS_DebrisWall_A.blend' },
  },
  {
    id: 'OBS_DEBRIS_SLAB_A',
    category: AssetCategory.Obstacle,
    // Built in Blender (tools/blender/assets/OBS_DebrisSlab_A.py, debris_kit.py) – greybox stays as fallback.
    source: { kind: 'glb', url: '/assets/models/obstacles/obs_debris_slab_a.glb', fallback: [
      box([0.2, 1.1, 0.6], -1.05, 0, 0, M.Concrete),
      box([0.2, 1.1, 0.6], 1.05, 0, 0, M.Concrete),
      box([2.2, 0.3, 0.8], 0, 1.1, 0, M.Concrete),
      box([2.2, 2, 0.8], 0, 1.4, 0, M.Concrete),
    ] },
    collision: GAMEPLAY_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: { description: 'Deckenplatte auf Schuttpfeilern mit Wandstück (Rutschen)', status: 'wip', triangleBudget: 3000, blenderSource: 'design/03_obstacles/OBS_DebrisSlab_A/OBS_DebrisSlab_A.blend' },
  },

  // ── Collectibles ─────────────────────────────────────────────────────────
  {
    id: 'COL_BEER_BOTTLE_A',
    category: AssetCategory.Collectible,
    // Built in Blender (tools/blender/assets/COL_BeerBottle_A.py) after the in-game views of the sheets.
    source: {
      kind: 'glb',
      url: '/assets/models/collectibles/col_beer_bottle_a.glb',
      fallback: bottleParts(M.Bottle),
    },
    collision: GAMEPLAY_COLLISION,
    preload: 'critical',
    instancing: 'instances',
    metadata: {
      description: 'Bierflasche (+1) im ructec-Design: braunes Glas, Etikett mit TR-Logo, schwebend',
      status: 'wip',
      triangleBudget: 1500,
      blenderSource: 'design/04_collectibles/COL_BeerBottle_A/COL_BeerBottle_A.blend',
    },
  },
  {
    id: 'COL_BEER_CRATE_A',
    category: AssetCategory.Collectible,
    // Built in Blender (tools/blender/assets/COL_BeerCrate_A.py) in ructec design.
    source: {
      kind: 'glb',
      url: '/assets/models/collectibles/col_beer_crate_a.glb',
      fallback: [box([0.88, 0.64, 0.66], 0, 0, 0, M.Crate)],
    },
    collision: GAMEPLAY_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Bierkiste (+5) im ructec-Design: CI-Orange, TR-Logo, 20 Flaschen',
      status: 'wip',
      triangleBudget: 2200,
      blenderSource: 'design/04_collectibles/COL_BeerCrate_A/COL_BeerCrate_A.blend',
    },
  },
  {
    id: 'PROP_MG_HAMMER_SET',
    category: AssetCategory.Prop,
    // Mini game "Hammer & Nagel" (tools/blender/assets/MG_HammerSet.py): claw hammer, nail, spruce beam – nodes
    // MG_HAMMER / MG_NAIL / MG_BEAM, real sizes. Not in the run: HammerStageView loads it on the first mini game.
    source: { kind: 'glb', url: '/assets/models/props/prop_mg_hammer_set.glb' },
    collision: { kind: 'none' },
    preload: 'lazy',
    instancing: 'clones',
    metadata: {
      description: 'Minispiel Hammer & Nagel: Klauenhammer, Nagel, Fichtenbalken',
      status: 'wip',
      triangleBudget: 8000,
      blenderSource: 'tools/blender/assets/MG_HammerSet.py',
    },
  },
  {
    id: 'COL_GOLDEN_BOTTLE_A',
    category: AssetCategory.Collectible,
    // Built in Blender (tools/blender/assets/COL_GoldenBottle_A.py): golden beer bottle, navy label, TR badge, "GOLD".
    source: { kind: 'glb', url: '/assets/models/collectibles/col_golden_bottle_a.glb', fallback: bottleParts(M.Golden, 1.25) },
    collision: GAMEPLAY_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Goldene Flasche (+10), einmal pro Lauf hinter der Rampe (SEG_GOLDEN_RAMP_60M)',
      status: 'wip',
      triangleBudget: 1000,
      blenderSource: 'design/04_collectibles/COL_GoldenBottle_A/COL_GoldenBottle_A.blend',
    },
  },
  {
    id: 'COL_TOOLBOX_A',
    category: AssetCategory.Collectible,
    // Built in Blender (tools/blender/assets/COL_Toolbox_A.py): red toolbox, ructec stickers (SEG_23 'Werkzeugkiste').
    source: { kind: 'glb', url: '/assets/models/collectibles/col_toolbox_a.glb', fallback: [box([0.85, 0.44, 0.36], 0, 0, 0, M.Barrier)] },
    collision: GAMEPLAY_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: { description: 'Werkzeugkiste (+10), rot, ructec-Aufkleber', status: 'wip', triangleBudget: 900, blenderSource: 'design/04_collectibles/COL_Toolbox_A/COL_Toolbox_A.blend' },
  },
  {
    id: 'COL_EXTRA_LIFE_A',
    category: AssetCategory.Collectible,
    // Built in Blender (tools/blender/assets/COL_BonusHelmet_A.py): Bonus-Helm in CI orange, TR logo sticker.
    source: {
      kind: 'glb',
      url: '/assets/models/collectibles/col_extra_life_a.glb',
      fallback: [sphere(0.3, -0.1, 0.4, 0, M.Heart), sphere(0.3, 0.1, 0.4, 0, M.Heart), box([0.26, 0.26, 0.2], 0, 0.12, 0, M.Heart, [0, 0, 45])],
    },
    collision: GAMEPLAY_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: { description: 'Bonus-Helm (Extra-Leben)', status: 'wip', triangleBudget: 1500, blenderSource: 'design/04_collectibles/COL_BonusHelmet_A/COL_BonusHelmet_A.blend' },
  },
  {
    id: 'COL_MAGNET_A',
    category: AssetCategory.Collectible,
    source: greybox([box([0.12, 0.4, 0.12], -0.15, 0, 0, M.Magnet), box([0.12, 0.4, 0.12], 0.15, 0, 0, M.Magnet), box([0.42, 0.12, 0.12], 0, 0.4, 0, M.Magnet)]),
    collision: GAMEPLAY_COLLISION,
    preload: 'lazy',
    instancing: 'instances',
    metadata: { description: 'Magnet (Power-up, vorbereitet)', status: 'greybox' },
  },
  {
    id: 'COL_TIME_BONUS_A',
    category: AssetCategory.Collectible,
    source: greybox([{ shape: 'cylinder', size: [0.45, 0.08, 0.45], position: [0, 0.3, 0], rotation: [90, 0, 0], material: M.TimeBonus }]),
    collision: GAMEPLAY_COLLISION,
    preload: 'lazy',
    instancing: 'instances',
    metadata: { description: 'Zeitbonus (Power-up, vorbereitet)', status: 'greybox' },
  },

  // ── Props / decoration ───────────────────────────────────────────────────
  {
    id: 'ENV_CONCRETE_BLOCK_A',
    category: AssetCategory.Environment,
    // Built in Blender (tools/blender/assets/ENV_ConcreteBlock_A.py); obstacle OBS_CONCRETE_BLOCK_A + building block.
    source: {
      kind: 'glb',
      url: '/assets/models/environment/env_concrete_block_a.glb',
      fallback: [box([1.6, 0.8, 0.8], 0, 0, 0, M.Concrete)],
    },
    collision: GAMEPLAY_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Betonblock 1,6 × 0,8 × 0,8 m mit Noppen und Kranöse',
      status: 'wip',
      triangleBudget: 400,
      blenderSource: 'design/05_environment/ENV_ConcreteBlock_A/ENV_ConcreteBlock_A.blend',
    },
  },
  {
    id: 'ENV_LIGHT_A',
    category: AssetCategory.Environment,
    // Built in Blender (tools/blender/assets/ENV_Light_A.py); SOCKET_LIGHT_L/R mark the night spot lights.
    source: {
      kind: 'glb',
      url: '/assets/models/environment/env_light_a.glb',
      fallback: [cylinder(0.08, 2.3, 0, 0, 0, M.Steel)],
    },
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Baustellenlampe: Stativ, Stromkasten, Teleskopmast, 2 LED-Fluter',
      status: 'wip',
      triangleBudget: 1500,
      blenderSource: 'design/05_environment/ENV_Light_A/ENV_Light_A.blend',
      tags: ['small'],
    },
  },
  {
    id: 'ENV_CONTAINER_A',
    category: AssetCategory.Environment,
    // Built in Blender (tools/blender/assets/ENV_Container_A.py), validated + published by build-asset.mjs.
    source: {
      kind: 'glb',
      url: '/assets/models/environment/env_container_a.glb',
      fallback: [box([6.06, 2.59, 2.44], 0, 0, 0, M.ContainerAlt)],
    },
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: '20-Fuß-Container blau, gebraucht (Rost, Schmutz, Beschriftung), TR-Plakette auf der Längsseite',
      status: 'wip',
      triangleBudget: 1500,
      blenderSource: 'design/05_environment/ENV_Container_A/ENV_Container_A.blend',
      tags: ['ground'],
    },
  },
  {
    id: 'PROP_PIPE_STACK_A',
    category: AssetCategory.Prop,
    source: greybox([
      pipeAlongZ(0.7, 3.6, -0.75, 0.35, 0, M.Concrete),
      pipeAlongZ(0.7, 3.6, 0, 0.35, 0, M.Concrete),
      pipeAlongZ(0.7, 3.6, 0.75, 0.35, 0, M.Concrete),
      pipeAlongZ(0.7, 3.6, -0.375, 0.95, 0, M.Concrete),
      pipeAlongZ(0.7, 3.6, 0.375, 0.95, 0, M.Concrete),
    ]),
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: { description: 'Betonrohre gestapelt', status: 'greybox', tags: ['ground'] },
  },
  {
    id: 'PROP_PALLET_GROUP_A',
    category: AssetCategory.Prop,
    source: greybox([
      box([1.2, 0.15, 1], -0.8, 0, 0, M.Wood),
      box([1.1, 0.7, 0.9], -0.8, 0.15, 0, M.Crate),
      box([1.2, 0.15, 1], 0.8, 0, 0.4, M.Wood),
      box([1.2, 0.15, 1], 0.8, 0.15, 0.4, M.Wood),
      box([1.1, 0.5, 0.9], 0.8, 0.3, 0.4, M.Concrete),
    ]),
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: { description: 'Paletten mit Material', status: 'greybox', tags: ['ground'] },
  },
  {
    id: 'ENV_SCAFFOLD_TOWER_A',
    category: AssetCategory.Environment,
    // Built in Blender (tools/blender/assets/ENV_ScaffoldTower_A.py) from the ENV_Scaffold_A kit.
    source: {
      kind: 'glb',
      url: '/assets/models/environment/env_scaffold_tower_a.glb',
      fallback: [box([2.72, 6, 1.64], 0, 0, 0, M.Steel)],
    },
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Gerüstturm 3 Ebenen (6 m): Rohr-Kupplungs-Gerüst, Holzbohlen, orange Bordbretter/Geländer',
      status: 'wip',
      triangleBudget: 2600,
      blenderSource: 'design/05_environment/ENV_ScaffoldTower_A/ENV_ScaffoldTower_A.blend',
      tags: ['ground', 'tall'],
    },
  },
  {
    id: 'ENV_CABLE_REEL_A',
    category: AssetCategory.Environment,
    // Built in Blender (tools/blender/assets/ENV_CableReel_A.py).
    source: {
      kind: 'glb',
      url: '/assets/models/environment/env_cable_reel_a.glb',
      fallback: [{ shape: 'cylinder', size: [1.6, 1.02, 1.6], position: [0, 0.8, 0], rotation: [0, 0, 90], material: M.Wood }],
    },
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Kabeltrommel Ø 1,6 m: Holzflansche, Mittelspannungskabel aufgewickelt, Stahlnabe, loses Kabelende',
      status: 'wip',
      triangleBudget: 900,
      blenderSource: 'design/05_environment/ENV_CableReel_A/ENV_CableReel_A.blend',
      tags: ['ground'],
    },
  },
  {
    id: 'OBS_CABLE_REEL_A',
    category: AssetCategory.Obstacle,
    // Built in Blender (tools/blender/assets/OBS_CableReel_A.py) from two ENV_CableReel_A drums.
    source: {
      kind: 'glb',
      url: '/assets/models/obstacles/obs_cable_reel_a.glb',
      fallback: [{ shape: 'cylinder', size: [1.6, 1.02, 1.6], position: [0, 0.8, 0], rotation: [0, 0, 90], material: M.Wood }],
    },
    collision: GAMEPLAY_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Kabeltrommel Ø 1,6 m in der Spur, Achse quer (Doppelsprung)',
      status: 'wip',
      triangleBudget: 800,
      blenderSource: 'design/03_obstacles/OBS_CableReel_A/OBS_CableReel_A.blend',
      tags: ['cable'],
    },
  },
  {
    id: 'ENV_CHEVRON_SIGN_A',
    category: AssetCategory.Environment,
    // Built in Blender (tools/blender/assets/ENV_ChevronSign_A.py): front "<<<<", back ">>>>".
    source: {
      kind: 'glb',
      url: '/assets/models/environment/env_chevron_sign_a.glb',
      fallback: [box([1.6, 0.5, 0.05], 0, 0.85, 0, M.Chevron), box([1.4, 0.09, 0.8], 0, 0, 0, M.Steel)],
    },
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Leitpfeiltafel 1,6 × 0,5 m, schwarz mit gelben Pfeilen, Pfosten, Gummifüße, Warnleuchte',
      status: 'wip',
      triangleBudget: 500,
      blenderSource: 'design/05_environment/ENV_ChevronSign_A/ENV_ChevronSign_A.blend',
      tags: ['guide'],
    },
  },
  {
    id: 'VEH_EXCAVATOR_A',
    category: AssetCategory.Vehicle,
    // Built in Blender (tools/blender/assets/VEH_01_Excavator.py) after design/02_vehicles/VEH_01_Excavator.
    // Rig nodes VEH_EXCAVATOR_A_PIVOT_UPPER / _BOOM / _STICK / _BUCKET for animation.
    source: {
      kind: 'glb',
      url: '/assets/models/vehicles/veh_excavator_a.glb',
      fallback: [box([3, 3, 4.5], 0, 0, -0.4, M.Steel), box([0.6, 3.5, 0.6], 0.25, 1, 3.5, M.Steel)],
    },
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Kettenbagger 24 t (8,5 × 3,0 m): ructec-Orange/Navy, Knickausleger, Glaskabine, Original-Logos, Rig für Animation',
      status: 'wip',
      triangleBudget: 14000,
      blenderSource: 'design/02_vehicles/VEH_01_Excavator/VEH_01_Excavator.blend',
      tags: ['backdrop'],
    },
  },
  {
    id: 'VEH_TRUCK_A',
    category: AssetCategory.Vehicle,
    // Built in Blender (tools/blender/assets/VEH_04_Truck.py) after design/02_vehicles/VEH_04_Truck.
    // Rig nodes VEH_TRUCK_A_PIVOT_BODY / _TAILGATE / _WHEEL_* for tipping, rolling and steering.
    source: {
      kind: 'glb',
      url: '/assets/models/vehicles/veh_truck_a.glb',
      fallback: [box([2.5, 2.2, 2.1], 0, 1.15, 3.3, M.Steel), box([2.5, 1.5, 5.6], 0, 1.2, -1.6, M.Steel)],
    },
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Baustellen-Kipper 6x4 (9,0 × 2,5 × 3,6 m): Kabine ructec-Blau, Stahlmulde mit Ladung, ructec.com, Rundumleuchten, Rig',
      status: 'wip',
      triangleBudget: 12000,
      blenderSource: 'design/02_vehicles/VEH_04_Truck/VEH_04_Truck.blend',
      tags: ['backdrop'],
    },
  },
  {
    id: 'VEH_CRANE_A',
    category: AssetCategory.Vehicle,
    // Built in Blender (tools/blender/assets/VEH_02_Crane.py) after design/02_vehicles/VEH_02_Crane.
    // Rig nodes VEH_CRANE_A_PIVOT_SLEW / _TROLLEY / _HOOK for slewing, trolley travel and hoisting.
    source: {
      kind: 'glb',
      url: '/assets/models/vehicles/veh_crane_a.glb',
      fallback: [box([1.8, 21.3, 1.8], 0, 0, 0, M.Steel), box([1.3, 1.1, 42], 0, 21.3, 9, M.Steel)],
    },
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Turmdrehkran 24 m, Ausleger 30 m + Gegenausleger 12 m (echtes Gitterwerk), Kabine, Laufkatze, Haken, Rig',
      status: 'wip',
      triangleBudget: 18000,
      blenderSource: 'design/02_vehicles/VEH_02_Crane/VEH_02_Crane.blend',
      tags: ['backdrop'],
    },
  },
  {
    id: 'VEH_FORKLIFT_A',
    category: AssetCategory.Vehicle,
    // Built in Blender (tools/blender/assets/VEH_03_Forklift.py); rig nodes VEH_FORKLIFT_A_PIVOT_MAST (tilt) / _FORKS (lift) / _LOAD (hideable) / _WHEEL_*.
    source: {
      kind: 'glb',
      url: '/assets/models/vehicles/veh_forklift_a.glb',
      fallback: [box([1.2, 1.1, 2.0], 0, 0.25, -0.25, M.Container), box([1.0, 2.2, 0.15], 0, 0, 0.98, M.Steel), box([0.7, 0.05, 1.15], 0, 0.08, 1.65, M.Steel)],
    },
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Gabelstapler 2,5 t (3,6 × 1,6 × 2,5 m): ructec-Orange, Schutzdach, Hubmast, Folien-Palettenlast, Rundumleuchte, Rig',
      status: 'wip',
      triangleBudget: 10000,
      blenderSource: 'design/02_vehicles/VEH_03_Forklift/VEH_03_Forklift.blend',
      tags: ['backdrop', 'hazard'],
    },
  },
  DOZER_ENTRY,
  {
    id: 'VEH_BUS_A',
    category: AssetCategory.Vehicle,
    // ructec-Bulli (tools/blender/assets/VEH_06_Bus.py, 2026-10-05): rig VEH_BUS_A_PIVOT_BODY (sprung body) and
    // ..._PIVOT_WHEEL_FL/FR/RL/RR – the BusPresenter moves them by name. The greybox stands in while the GLB loads.
    source: {
      kind: 'glb',
      url: '/assets/models/vehicles/veh_bus_a.glb',
      fallback: [
        { shape: 'box', size: [1.7, 1.3, 4.1], position: [0, 1.0, 0], material: M.Container },
        { shape: 'box', size: [1.3, 0.12, 2.9], position: [0, 2.0, 0.05], material: M.Wood },
      ],
    },
    // the gameplay box is BusConfig.body (the van as the rider's collider)
    collision: { kind: 'gameplay-data' },
    preload: 'run',
    instancing: 'clones',
    metadata: {
      description: 'ructec-Bulli: Kastenwagen in ructec-Dunkelblau, Logos vorne/seitlich/hinten, Dachträger mit Holz, Rig Karosserie + 4 Räder',
      status: 'wip',
      triangleBudget: 36000,
      blenderSource: 'design/02_vehicles/VEH_06_Bus/source/VW_T1_Panel_Van.blend',
      tags: ['vehicle', 'bus'],
    },
  },
  {
    id: 'ENV_BUS_RAMP_A',
    category: AssetCategory.Environment,
    // Timber ramp on lanes 1 and 2 of SEG_BUS_RAMP_40M (centred there, RAMP_X): 4.4 m wide, 6 m long, rising to
    // 1.08 m (slope 0.18), origin at its foot – the oncoming lane 0 stays flat.
    source: greybox([
      { shape: 'box', size: [4.4, 0.1, 6.1], position: [0, 0.5, 3], rotation: [-10.2, 0, 0], material: M.Wood },
      { shape: 'box', size: [4.4, 1.08, 0.12], position: [0, 0.54, 6.0], material: M.Wood },
      { shape: 'box', size: [0.12, 1.08, 6], position: [-2.2, 0.54, 3], material: M.Steel },
      { shape: 'box', size: [0.12, 1.08, 6], position: [2.2, 0.54, 3], material: M.Steel },
      { shape: 'box', size: [4.4, 0.02, 0.3], position: [0, 1.1, 5.85], material: M.Chevron },
    ]),
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Holzrampe für den Bulli auf den Spuren 1–2 (4,4 × 6 m, 1,08 m hoch) mit Warnstreifen an der Kante',
      status: 'wip',
      triangleBudget: 200,
      tags: ['bus', 'ramp'],
    },
  },
  {
    id: 'ENV_BUS_KICKER_A',
    category: AssetCategory.Environment,
    // Small kicker on lanes 1 and 2 of SEG_BUS_KICKER_40M: 4.4 m wide, 3 m long, rising to 0.5 m, origin at its foot.
    source: greybox([
      { shape: 'box', size: [4.4, 0.08, 3.05], position: [0, 0.24, 1.5], rotation: [-9.5, 0, 0], material: M.Wood },
      { shape: 'box', size: [4.4, 0.5, 0.1], position: [0, 0.25, 3.0], material: M.Wood },
      { shape: 'box', size: [0.1, 0.5, 3], position: [-2.2, 0.25, 1.5], material: M.Steel },
      { shape: 'box', size: [0.1, 0.5, 3], position: [2.2, 0.25, 1.5], material: M.Steel },
      { shape: 'box', size: [4.4, 0.02, 0.25], position: [0, 0.52, 2.88], material: M.Chevron },
    ]),
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Kleiner Holzkicker für den Bulli auf den Spuren 1–2 (4,4 × 3 m, 0,5 m hoch)',
      status: 'wip',
      triangleBudget: 160,
      tags: ['bus', 'ramp'],
    },
  },
  {
    id: 'ENV_BUS_FINALE_RAMP_A',
    category: AssetCategory.Environment,
    // Big finale ramp on lanes 1 and 2 of SEG_BUS_FINALE_60M: 4.4 m wide, 9 m long, rising to 1.62 m, origin at its foot.
    source: greybox([
      { shape: 'box', size: [4.4, 0.12, 9.15], position: [0, 0.76, 4.5], rotation: [-10.2, 0, 0], material: M.Wood },
      { shape: 'box', size: [4.4, 1.62, 0.14], position: [0, 0.81, 9.0], material: M.Wood },
      { shape: 'box', size: [0.14, 1.62, 9], position: [-2.2, 0.81, 4.5], material: M.Steel },
      { shape: 'box', size: [0.14, 1.62, 9], position: [2.2, 0.81, 4.5], material: M.Steel },
      { shape: 'box', size: [4.4, 0.02, 0.4], position: [0, 1.65, 8.8], material: M.Chevron },
    ]),
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Große Finalrampe für den Bulli auf den Spuren 1–2 (4,4 × 9 m, 1,62 m hoch) mit Warnstreifen',
      status: 'wip',
      triangleBudget: 200,
      tags: ['bus', 'ramp'],
    },
  },
  {
    id: 'ENV_BUS_CARGO_A',
    category: AssetCategory.Environment,
    // A piece of the Bulli's roof load thrown off (BusPresenter): a strapped bundle of boards like VEH_BUS_A_PIVOT_CARGO_*,
    // origin at its centre.
    source: greybox([
      { shape: 'box', size: [0.5, 0.11, 1.3], position: [0, 0, 0], material: M.Wood },
      { shape: 'box', size: [0.52, 0.125, 0.05], position: [0, 0, -0.38], material: M.MachineYellow },
      { shape: 'box', size: [0.52, 0.125, 0.05], position: [0, 0, 0.38], material: M.MachineYellow },
    ]),
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Abgeworfenes Ladungsteil vom Bulli-Dach: Bretterbündel mit zwei Spanngurten',
      status: 'wip',
      triangleBudget: 60,
      tags: ['bus', 'cargo'],
    },
  },
  {
    id: 'VEH_BUS_B',
    category: AssetCategory.Vehicle,
    // The same Bulli for the rival and the oncoming traffic of the ride (tools/blender/assets/VEH_08_BusRival.py, variant
    // of VEH_06_Bus.py): no ructec logos, paint MAT_BUSB_PAINT recoloured per car (BusTrafficPresenter), sewer pipes on
    // the rack; rig VEH_BUS_B_PIVOT_BODY / _WHEEL_FL/FR/RL/RR.
    source: {
      kind: 'glb',
      url: '/assets/models/vehicles/veh_bus_b.glb',
      fallback: [
        { shape: 'box', size: [1.7, 1.3, 4.1], position: [0, 1.0, 0], material: M.ContainerAlt },
        { shape: 'box', size: [1.2, 0.3, 2.8], position: [0, 2.1, 0.05], material: M.MachineYellow },
      ],
    },
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'clones',
    metadata: {
      description: 'Konkurrenz-Bulli: gleicher Kastenwagen ohne Logos, Lack je Fahrzeug, Dachträger mit Kanalrohren, Rig Karosserie + 4 Räder',
      status: 'wip',
      triangleBudget: 36000,
      blenderSource: 'design/02_vehicles/VEH_06_Bus/source/VW_T1_Panel_Van.blend',
      tags: ['vehicle', 'bus', 'traffic'],
    },
  },  {
    id: 'OBS_CRANE_LOAD_A',
    category: AssetCategory.Obstacle,
    // Built in Blender (tools/blender/assets/OBS_CraneLoad_A.py) after SEG_09; rig node OBS_CRANE_LOAD_A_PIVOT_SWING at the rope top (y 6.4).
    source: {
      kind: 'glb',
      url: '/assets/models/obstacles/obs_crane_load_a.glb',
      fallback: [box([6, 0.3, 0.63], 0, 0.025, 0, M.Steel), box([0.05, 6, 0.05], 0, 0.4, 0, M.Steel)],
    },
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Kranlast Stahlträger: 2 × HEB 300, 6 m, Zweistrang-Gehänge, Unterflasche, Seil bis 6,4 m (schwenkt über alle Spuren)',
      status: 'wip',
      triangleBudget: 3000,
      blenderSource: 'design/03_obstacles/OBS_CraneLoad_A/OBS_CraneLoad_A.blend',
      tags: ['hazard', 'crane'],
    },
  },
  {
    id: 'OBS_CRANE_LOAD_B',
    category: AssetCategory.Obstacle,
    // Built in Blender (tools/blender/assets/OBS_CraneLoad_B.py) after SEG_09; rig node OBS_CRANE_LOAD_B_PIVOT_SWING at the rope top (y 6.4).
    source: {
      kind: 'glb',
      url: '/assets/models/obstacles/obs_crane_load_b.glb',
      fallback: [box([1.6, 1.0, 1.2], 0, 0, 0, M.Concrete), box([0.05, 5.4, 0.05], 0, 1.0, 0, M.Steel)],
    },
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Kranlast Betonblock 1,6 × 1,0 × 1,2 m am Vierstrang-Kettengehänge, Unterflasche, Seil bis 6,4 m (senkt sich in eine Spur oder pendelt quer über alle Spuren)',
      status: 'wip',
      triangleBudget: 3000,
      blenderSource: 'design/03_obstacles/OBS_CraneLoad_B/OBS_CraneLoad_B.blend',
      tags: ['hazard', 'crane'],
    },
  },
  {
    id: 'OBS_PALLET_RACK_A',
    category: AssetCategory.Obstacle,
    // Built in Blender (tools/blender/assets/OBS_PalletRack_A.py) after SEG_11 "Regal (durchrutschen)".
    source: {
      kind: 'glb',
      url: '/assets/models/obstacles/obs_pallet_rack_a.glb',
      fallback: [box([2.2, 2.0, 1.1], 0, 1.6, 0, M.Container), box([0.08, 1.6, 1.1], -1.05, 0, 0, M.Steel), box([0.08, 1.6, 1.1], 1.05, 0, 0, M.Steel)],
    },
    collision: GAMEPLAY_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Palettenregal: blaue Rahmen, orange Traversen ab 1,6 m (Rutschen), ructec-Kartons und Folienpaletten',
      status: 'wip',
      triangleBudget: 5000,
      blenderSource: 'design/03_obstacles/OBS_PalletRack_A/OBS_PalletRack_A.blend',
      tags: ['rack'],
    },
  },
  {
    id: 'ENV_LIGHT_MAST_A',
    category: AssetCategory.Environment,
    // Built in Blender (tools/blender/assets/ENV_LightMast_A.py) – light tower of the 3D views and SEG_22; floods face +z.
    source: {
      kind: 'glb',
      url: '/assets/models/environment/env_light_mast_a.glb',
      fallback: [box([1.3, 1.1, 2.0], 0, 0.5, 0, M.Container), box([0.15, 6.4, 0.15], 0, 1.6, -0.55, M.Steel), box([1.5, 0.4, 0.2], 0, 7.9, -0.47, M.Steel)],
    },
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Lichtmast auf Anhänger: ructec-Orange, Teleskopmast 8,3 m, 4 LED-Fluter (Nacht)',
      status: 'wip',
      triangleBudget: 5000,
      blenderSource: 'design/05_environment/ENV_LightMast_A/ENV_LightMast_A.blend',
    },
  },
  {
    id: 'ENV_GENERATOR_A',
    category: AssetCategory.Environment,
    // Built in Blender (tools/blender/assets/ENV_Generator_A.py) – "Stromaggregat" of SEG_14 / SEG_22.
    source: {
      kind: 'glb',
      url: '/assets/models/environment/env_generator_a.glb',
      fallback: [box([1.0, 1.35, 2.2], 0, 0, 0, M.Container)],
    },
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Stromaggregat 40 kVA auf Kufen: ructec-Orange, Lüftungsgitter, Schaltschrank, Auspuff, TR-Logo',
      status: 'wip',
      triangleBudget: 2500,
      blenderSource: 'design/05_environment/ENV_Generator_A/ENV_Generator_A.blend',
    },
  },
  {
    id: 'ENV_WARNING_LIGHT_A',
    category: AssetCategory.Environment,
    // Built in Blender (tools/blender/assets/ENV_WarningLight_A.py); lens = separate mesh ENV_WARNING_LIGHT_A_LENS (flashable).
    source: {
      kind: 'glb',
      url: '/assets/models/environment/env_warning_light_a.glb',
      fallback: [box([0.42, 0.08, 0.42], 0, 0, 0, M.Barrier), box([0.05, 1.0, 0.05], 0, 0.08, 0, M.Barrier), box([0.2, 0.2, 0.14], 0, 1.1, 0, M.Golden)],
    },
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Baustellen-Warnleuchte (gelb, Fuß + rot/weißer Pfosten), Linse leuchtet',
      status: 'wip',
      triangleBudget: 900,
      blenderSource: 'design/05_environment/ENV_WarningLight_A/ENV_WarningLight_A.blend',
    },
  },
  {
    id: 'ENV_SKIP_CONTAINER_A',
    category: AssetCategory.Environment,
    // Built in Blender (tools/blender/assets/ENV_SkipContainer_A.py): 7 m³ skip with building rubble.
    source: { kind: 'glb', url: '/assets/models/environment/env_skip_container_a.glb', fallback: [box([2.1, 1.4, 3.6], 0, 0, 0, M.Container)] },
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: { description: 'Absetzmulde 7 m³ in ructec-Orange mit Bauschutt und Bewehrung', status: 'wip', triangleBudget: 3000, blenderSource: 'design/05_environment/ENV_SkipContainer_A/ENV_SkipContainer_A.blend' },
  },
  {
    id: 'ENV_REBAR_BUNDLE_A',
    category: AssetCategory.Environment,
    // Built in Blender (tools/blender/assets/ENV_RebarBundle_A.py): 6 m rebar bundles on dunnage.
    source: { kind: 'glb', url: '/assets/models/environment/env_rebar_bundle_a.glb', fallback: [box([6.0, 0.4, 1.4], 0, 0, 0, M.Steel)] },
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: { description: 'Baustahl-Bündel (6 m) auf Kanthölzern, gebunden, mit Lieferanhänger', status: 'wip', triangleBudget: 6000, blenderSource: 'design/05_environment/ENV_RebarBundle_A/ENV_RebarBundle_A.blend' },
  },
  ...ROADSIDE_BAYS,
  AD_GANTRY_ENTRY,
  {
    id: 'ENV_CRANE_A',
    category: AssetCategory.Environment,
    // Built in Blender (tools/blender/assets/ENV_Crane_A.py) – backdrop of every sheet.
    source: {
      kind: 'glb',
      url: '/assets/models/environment/env_crane_a.glb',
      fallback: [box([1.6, 47, 1.6], 0, 0, 0, M.Steel)],
    },
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Turmdrehkran: Mast 45 m, Ausleger 50 m, Gegenausleger mit Ballast, Turmspitze, Kabine, Laufkatze',
      status: 'wip',
      triangleBudget: 900,
      blenderSource: 'design/05_environment/ENV_Crane_A/ENV_Crane_A.blend',
      tags: ['backdrop'],
    },
  },
  {
    id: 'BLD_SHELL_A',
    category: AssetCategory.Building,
    // Built in Blender (tools/blender/assets/BLD_Shell_A.py) – scaffolded shell building next to the track.
    source: {
      kind: 'glb',
      url: '/assets/models/buildings/bld_shell_a.glb',
      fallback: [box([18, 12, 12], 0, 0, 0, M.Concrete)],
    },
    collision: NO_COLLISION,
    preload: 'run',
    instancing: 'instances',
    metadata: {
      description: 'Rohbau 18 × 12 m: 4 Geschosse, Stützen, Treppenkern, Mauerwerk, Absturzsicherung, Fassadengerüst',
      status: 'wip',
      triangleBudget: 1500,
      blenderSource: 'design/05_environment/BLD_Shell_A/BLD_Shell_A.blend',
      tags: ['backdrop'],
    },
  },
];
